"use client";

// WO-105：展屏只有公开读取；关闭响应优先于离线缓存。
import { memo, useEffect, useRef, useState } from "react";
import { getScreen, type PublicScreen } from "../../lib/public-api";
import { publicScreenPayload } from "./public-screen-payload";
import { SeasonArt } from "../../components/season-art";
import { seasonOf } from "../../components/qy/season";
import { ganzhiYear, termName, WEEKDAY } from "../../lib/calendar";

const CACHE_KEY = "public-portal-screen-wo105-v1";
const POLL_MS = 60_000;
const SLIDE_MS = 12_000;

function readCache(): PublicScreen | null {
  try { return publicScreenPayload(JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null")); }
  catch { return null; }
}
function storeCache(data: PublicScreen | null) {
  try {
    if (data) localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    else localStorage.removeItem(CACHE_KEY);
  } catch { /* 禁用存储或空间不足不影响在线展示。 */ }
}
function time(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false });
}

// 天幕：夜色版画、节气、干支年、日期与时钟。只用 setTimeout 对准整分，不开 setInterval；挂载后才渲染，避免服务端与客户端时间不一致。
function ScreenSky() {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    let timer = 0;
    const tick = () => { const date = new Date(); setNow(date); timer = window.setTimeout(tick, 60_000 - (date.getSeconds() * 1000 + date.getMilliseconds())); };
    tick();
    return () => window.clearTimeout(timer);
  }, []);
  if (!now) return <div className="screen-sky" aria-hidden="true" />;
  const year = ganzhiYear(now);
  return <div className="screen-sky">
    <SeasonArt season={seasonOf(now)} className="screen-sky__art" />
    <div className="screen-date">
      <p className="screen-date__term"><strong>{termName(now)}</strong>{year && <span>{year}</span>}</p>
      <p className="screen-date__day">{now.getMonth() + 1} 月 {now.getDate()} 日 · 星期{WEEKDAY[now.getDay()]}</p>
      <p className="screen-date__clock">{String(now.getHours()).padStart(2, "0")}<span>:</span>{String(now.getMinutes()).padStart(2, "0")}</p>
    </div>
  </div>;
}

// 相同 etag 保留同一个数据对象；状态条变化也不重绘内容区。
const PublicDisplay = memo(function PublicDisplay({ data }: { data: PublicScreen }) {
  const [position, setPosition] = useState(0);
  useEffect(() => {
    if (data.items.length < 2) return;
    const timer = window.setInterval(() => setPosition(index => (index + 1) % data.items.length), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [data]);
  const item = data.items[position % data.items.length];
  return <div className="screen-content" data-testid="screen-content" data-etag={data.etag}>
    <header className="screen-masthead">
      <div><p className="screen-kicker">公众展屏</p><h1>{data.profile.name}</h1></div>
      <p className="screen-motto">与光同建<span>饮水思源 · 建设为民</span></p>
    </header>
    {data.profile.syntheticNotice && <p className="screen-notice" data-testid="synthetic-notice">{data.profile.syntheticNotice}</p>}
    <section className="screen-story" aria-label="公开内容轮播" aria-roledescription="轮播">
      {item ? <article key={item.id} data-testid="screen-slide" data-public-ref={item.id}>
        <div className="screen-story-meta"><span>{item.channelTitle}</span><time dateTime={item.date}>{item.date}</time></div>
        <h2>{item.title}</h2><p className="screen-digest">{item.digest}</p>
      </article> : <div className="screen-empty"><h2>暂无获准展屏的内容</h2><p>内容经单独审核放行后，将在这里轮播。</p></div>}
      <div className="screen-progress" aria-hidden="true">{data.items.map((entry, index) => <span key={entry.id} className={index === position % data.items.length ? "current" : ""} />)}</div>
      {data.items.length > 1 && item && <div className="screen-bar" aria-hidden="true" key={`bar-${item.id}`}><span style={{ animationDuration: `${SLIDE_MS}ms` }} /></div>}
    </section>
    <section className="screen-metrics" aria-label="公开披露指标">
      {data.profile.snapshot.metrics.length ? data.profile.snapshot.metrics.map((metric, index) => <article className="screen-metric" key={`${metric.label}-${index}`} data-testid="screen-metric">
        <h2>{metric.label}</h2><p className="screen-metric-value" data-testid="metric-value">{metric.value.toLocaleString("zh-CN")}</p>
        <p className="screen-caliber">口径：{metric.caliber}</p>
        <p>统计时点 {time(metric.validAt!)}<br />计算时点 {time(metric.computedAt!)} · 口径版本 {metric.definitionVersion}</p>
      </article>) : <p className="screen-no-metrics">暂无获准公开的指标</p>}
    </section>
  </div>;
});

export default function Screen() {
  const [data, setData] = useState<PublicScreen | null>(null);
  const [state, setState] = useState<"loading" | "online" | "offline" | "closed">("loading");
  const latest = useRef<PublicScreen | null>(null);
  useEffect(() => {
    let disposed = false;
    let pending: AbortController | null = null;
    let cached = readCache();
    const load = async () => {
      if (pending) return;
      const controller = new AbortController();
      pending = controller;
      const result = await getScreen({ signal: controller.signal });
      if (disposed) return;
      pending = null;
      if (result.status === "unavailable" || (result.status === "error" && [401, 403].includes(result.httpStatus ?? 0))) {
        latest.current = null;
        cached = null;
        storeCache(null);
        setData(null);
        setState("closed");
        return;
      }
      const payload = result.status === "ok" ? publicScreenPayload(result.data) : null;
      if (payload) {
        if (latest.current?.etag !== payload.etag) {
          latest.current = payload;
          cached = payload;
          storeCache(payload);
          setData(payload);
        }
        setState("online");
      } else {
        // 缓存只在读取失败时恢复，避免已关闭展屏在首次校验前闪出旧内容。
        if (!latest.current && cached) {
          latest.current = cached;
          setData(cached);
        }
        setState(previous => previous === "closed" ? "closed" : "offline");
      }
    };
    void load();
    const timer = window.setInterval(() => { void load(); }, POLL_MS);
    return () => { disposed = true; pending?.abort(); window.clearInterval(timer); };
  }, []);

  return <main className="public-screen" data-testid="public-screen">
    <ScreenSky />
    {data ? <PublicDisplay key={data.etag} data={data} /> : <div className="screen-message">
      <p className="screen-kicker">公众展屏</p>
      <h1>{state === "closed" ? "展屏尚未开通" : state === "offline" ? "暂时无法载入展屏" : "正在载入公开内容"}</h1>
      <p>{state === "closed" ? "展屏内容将在单独审核放行后提供。" : state === "offline" ? "当前离线，尚无成功载入的公开内容。连接恢复后将自动重试。" : "请稍候"}</p>
    </div>}
    {data && <footer className="screen-status" data-testid="screen-status" role="status">
      数据时点 {time(data.generatedAt)}{state === "offline" ? "，当前离线" : " · 在线"}
    </footer>}
  </main>;
}
